import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Get WebSocket URL from environment
const getWebSocketUrl = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  // Convert http(s) to ws(s)
  const wsUrl = apiUrl.replace(/^http/, 'ws');
  return wsUrl;
};

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Notification handlers for different types
  const notificationHandlers = {
    // Chat notifications
    new_message: (data) => {
      toast.info(`💬 ${data.title}`, { description: data.message });
    },
    message_reply: (data) => {
      toast.info(`💬 ${data.title}`, { description: data.message });
    },
    
    // Visitor notifications
    visitor_checked_in: (data) => {
      toast.success(`🚪 ${data.title}`, { description: data.message });
    },
    visitor_registered: (data) => {
      toast.info(`📋 ${data.title}`, { description: data.message });
    },
    
    // Parcel notifications
    parcel_received: (data) => {
      toast.success(`📦 ${data.title}`, { description: data.message });
    },
    parcel_collected: (data) => {
      toast.info(`✅ ${data.title}`, { description: data.message });
    },
    
    // Notice notifications
    new_notice: (data) => {
      toast.info(`📢 ${data.title}`, { description: data.message });
    },
    
    // Maintenance notifications
    ticket_created: (data) => {
      toast.info(`🔧 ${data.title}`, { description: data.message });
    },
    ticket_updated: (data) => {
      toast.info(`🔧 ${data.title}`, { description: data.message });
    },
    ticket_resolved: (data) => {
      toast.success(`✅ ${data.title}`, { description: data.message });
    },
    
    // Voting notifications
    new_poll: (data) => {
      toast.info(`🗳️ ${data.title}`, { description: data.message });
    },
    poll_closed: (data) => {
      toast.info(`📊 ${data.title}`, { description: data.message });
    },
    
    // Booking notifications
    booking_confirmed: (data) => {
      toast.success(`📅 ${data.title}`, { description: data.message });
    },
    
    // Payment notifications
    invoice_created: (data) => {
      toast.info(`💳 ${data.title}`, { description: data.message });
    },
    payment_received: (data) => {
      toast.success(`✅ ${data.title}`, { description: data.message });
    },
    
    // Event notifications
    new_event: (data) => {
      toast.info(`🎉 ${data.title}`, { description: data.message });
    },
    event_reminder: (data) => {
      toast.info(`⏰ ${data.title}`, { description: data.message });
    },

    // Membership notifications
    member_request: (data) => {
      // Skip "refresh signal" silent broadcasts (data.data.refresh === true)
      if (data?.data?.refresh) return;
      toast.info(`👤 ${data.title}`, { description: data.message });
    },
    member_approved: (data) => {
      toast.success(`✅ ${data.title}`, { description: data.message });
    },
    member_rejected: (data) => {
      toast.error(`⛔ ${data.title}`, { description: data.message });
    },
  };

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      // Ignore connection confirmation and pong messages
      if (data.type === 'connected' || data.type === 'pong') {
        return;
      }
      
      // Check if notification is targeted to specific users
      if (data.target_users && data.target_users.length > 0) {
        if (!data.target_users.includes(user?.user_id)) {
          return; // Not for this user
        }
      }
      
      // Add to notifications list
      setNotifications(prev => [data, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
      
      // Call appropriate handler
      const handler = notificationHandlers[data.type];
      if (handler) {
        handler(data);
      } else {
        // Generic notification
        toast.info(data.title, { description: data.message });
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, [user]);

  const connect = useCallback(() => {
    if (!user?.hoa_id) return;
    
    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = `${getWebSocketUrl()}/api/ws/connect/${user.hoa_id}?user_id=${user.user_id}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };
      
      ws.onmessage = handleMessage;
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [user, handleMessage]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    setIsConnected(false);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Connect when user is authenticated
  useEffect(() => {
    if (user?.hoa_id && user?.user_id) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [user?.hoa_id, user?.user_id, connect, disconnect]);

  const value = {
    isConnected,
    notifications,
    unreadCount,
    clearNotifications,
    markAsRead,
    connect,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export default WebSocketContext;
